import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@hero/web-lib';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
      },
      size: {
        default: 'px-2.5 py-0.5 text-xs gap-2',
        small: 'px-2 py-0.5 text-xs gap-1.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const ICON_SIZE_CLASS: Record<string, string> = {
  default: '!h-5 !w-5',
  small: '!h-4 !w-4',
};

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  icon?: React.ReactElement;
}

function Badge({ className, variant, size, icon, children, ...props }: BadgeProps) {
  const resolvedSize = size ?? 'default';
  const styledIcon =
    icon && React.isValidElement(icon)
      ? React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
          className: cn(ICON_SIZE_CLASS[resolvedSize], (icon.props as { className?: string }).className),
        })
      : null;

  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {styledIcon}
      {children}
    </div>
  );
}

export { Badge, badgeVariants };
